import { ReactElement, Ref, forwardRef } from "react";

import {
  Select,
  type SelectProps,
  type SelectInstance,
} from "_/components/select";
import { UserAvatar } from "_/components/avatar";
import { User, useUsers } from "_/data/users";
import { useCurrentOrg, useOrgUsers } from "_/data/orgs";
import { Uuid } from "_/types";

interface OptionType {
  value: Uuid;
  label: string;
  user: User;
}

type UserSelectionProps = Omit<SelectProps<OptionType>, "options"> & {
  users: User[];
};

type AllUserSelectionProps = Omit<SelectProps<OptionType>, "options">;

type OrgUserSelectionProps = Omit<SelectProps<OptionType>, "options"> & {
  orgId?: Uuid;
};

/**
 * Select component for displaying users list dropdown.
 *
 * This will by default show all users that are visible by the currently logged in user.
 */
export const UserSelect = forwardRef(function UserSelect(
  props: UserSelectionProps,
  ref: Ref<SelectInstance<OptionType, false>> | undefined
): ReactElement {
  const options = props.users.map((u) => ({
    value: u.id,
    label: u.name,
    user: u,
  }));

  const formatter = (opt: (typeof options)[number]) => {
    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <UserAvatar resource={opt.user} size="small" />
        <span style={{ marginLeft: "10px" }}>{opt.user.name}</span>
      </div>
    );
  };

  return (
    <Select
      {...props}
      ref={ref}
      options={options}
      formatOptionLabel={formatter}
    />
  );
});

export const AllUserSelect = forwardRef(function AllUserSelect(
  props: AllUserSelectionProps,
  ref: Ref<SelectInstance<OptionType>> | undefined
): ReactElement {
  const { data: users = [] } = useUsers();

  return <UserSelect {...props} ref={ref} users={users} />;
});

export const OrgUserSelect = forwardRef(function OrgUserSelect(
  { orgId, ...props }: OrgUserSelectionProps,
  ref: Ref<SelectInstance<OptionType>> | undefined
): ReactElement {
  const { data: currentOrg } = useCurrentOrg();
  const { data: orgUsers = [] } = useOrgUsers(orgId ?? currentOrg.id);

  const users = orgUsers.map((ou) => ou.user);

  return <UserSelect {...props} ref={ref} users={users} />;
});
